import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ClickableTextPanel } from "@/Common/Components/ClickableText/ClickableTextPanel";
import { SimpleTextareaResizable } from "@/Common/Components/Inputs/SimpleTextareaResizable";
import { TextboxMaskedControl } from "@/Common/Components/Inputs/TextboxMaskedControl";
import { Popup } from "@/Common/Components/Modals/Popup";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { InboundFaxDetails } from "@/Documents/Models/InboundFax/InboundFaxDetails";

import "./ReturnFaxToSenderPopup.css";

interface IReturnFaxToSenderPopupProps {
    title: string;
    initialFaxNumber: string;
    clickableTextContext: string;
    onAccept: (faxDetails: InboundFaxDetails) => void;
    onCancel: () => void;
}

class ReturnFaxToSenderPopupState {
    public faxNumber: string;
    public notes: string;
    public isNoteClickableTextShown: boolean;
}

class ReturnFaxToSenderPopup extends BaseReactComponent<IReturnFaxToSenderPopupProps, ReturnFaxToSenderPopupState> {
    private phoneValidationRule = new ValidationHelper().getPhoneNumberValidationRule(cacheStore.SiteCache[0].Model.Adresses[0].CountryLookupId, null);
    private phoneMask: string = this.phoneValidationRule?.EditMaskPattern ?? "";
    private validationRegex: string = this.phoneValidationRule ? this.phoneValidationRule.ValidationRegexPattern : "";

    constructor(props: IReturnFaxToSenderPopupProps) {
        super(props);

        const initialState = new ReturnFaxToSenderPopupState();
        initialState.faxNumber = this.props.initialFaxNumber;
        initialState.notes = "";
        this.state = initialState;
    }

    private editFaxNumber(val: string): void {
        this.mutateState(s => s.faxNumber = val);
    }

    private editNotes(e: React.ChangeEvent<HTMLTextAreaElement>): void {
        const val = e.target.value;
        this.mutateState(s => s.notes = val);
    }

    public render(): JSX.Element {
        const returnFaxToSenderFaxField = "returnFaxToSenderPopup_FaxNumberField";
        const returnFaxToSenderNotesField = "returnFaxToSenderPopup_NotesField";
        const regExp: RegExp = new RegExp(this.validationRegex, "i");
        const isFaxNumberValid: boolean = regExp.test(this.state.faxNumber) && !!this.state.faxNumber?.trim();

        return (
            <div onClick={() => this.containerClick()} className="return-fax-to-sender-popup-overlay">
                <Popup
                    cssClass="return-fax-to-sender-popup"
                    title={this.props.title}
                    draggable
                    onCloseDelegate={() => this.props.onCancel()}
                    manageOverlay
                    disableAutoStyling={false}
                    isOverflowVisible={true}
                    titleLowerCase
                    standardFooterProps={{
                        cancelButton: {
                            label: cacheStore.GetString("CancelLabel"),
                            onClick: () => { this.props.onCancel(); },
                            className: "btn btn-default push-right-5"
                        },
                        okButton: {
                            label: cacheStore.GetString("FaxVerbLabel"),
                            onClick: () => {
                                this.props.onAccept({ faxNumber: this.state.faxNumber, notes: this.state.notes });
                            },
                            disabled: !isFaxNumberValid,
                            className: "btn btn-primary return-to-sender-fax-right"
                        }
                    }}
                >
                    <div role="form">
                        <label htmlFor={returnFaxToSenderFaxField}>{cacheStore.GetString("FaxNumberLabel")}</label>
                        <TextboxMaskedControl
                            isValid
                            allowEmpty={false}
                            returnMaskedValue={false}
                            id={returnFaxToSenderFaxField}
                            value={this.state.faxNumber}
                            onChangeFunction={val => this.editFaxNumber(val)}
                            item={null}
                            mask={this.phoneMask}
                        />

                        <label htmlFor={returnFaxToSenderNotesField}>{cacheStore.GetString("NotesLabel")}</label>
                        <SimpleTextareaResizable
                            id={returnFaxToSenderNotesField}
                            className="form-control textarea-resize-vertical"
                            rows={4}
                            value={this.state.notes}
                            onChange={e => this.editNotes(e)}
                            maxLength={1000}
                        />
                        {this.state.isNoteClickableTextShown && (
                            <div className="clickable-text-container" style={{ width: "500px" }}>
                                <ClickableTextPanel
                                    cssClass="text-helper-container"
                                    context={this.props.clickableTextContext}
                                    inputElementId={returnFaxToSenderNotesField}
                                    onCloseEvent={() => this.toggleClickableNotesVisibility()}
                                />
                            </div>
                        )}

                        <a
                            className="clickable-text-link"
                            onClick={() => this.toggleClickableNotesVisibility()}
                        >
                            {cacheStore.GetString("SelectFromListLabel")}
                        </a>
                    </div>
                </Popup>
            </div>
        );
    }

    public containerClick(): void {
        this.state.isNoteClickableTextShown && this.toggleClickableNotesVisibility();
    }

    private toggleClickableNotesVisibility(): void {
        this.mutateState(s => s.isNoteClickableTextShown = !s.isNoteClickableTextShown);
    }
}

export { ReturnFaxToSenderPopup };
